import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CalendarCheck,
  Phone,
  Mail,
  ShieldCheck,
  Clock,
  ExternalLink,
  UserPlus,
  MousePointerClick,
  CheckCircle2,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { BOX_IMAGES } from '@/data/images';
import { API } from '@/lib/api';
import { DeciplusIframe } from '@/components/DeciplusIframe';

const EASE = [0.22, 1, 0.36, 1];

const DEFAULT_CFG = {
  embed_url: 'https://member-app.deciplus.pro/cf-saintchamond',
  provider_name: 'Deciplus',
  enabled: true,
  height_px: 1100,
  fallback_cta_label: 'Me contacter pour réserver',
  fallback_message:
    "Notre système de réservation en ligne arrive très bientôt. En attendant, contactez-nous directement, nous répondons sous 24h.",
};

const HOW_STEPS = [
  {
    icon: UserPlus,
    num: '01',
    title: 'Crée ton compte',
    desc: "Première visite ? Quelques infos, une adresse email, et c'est parti. Tes identifiants Deciplus te serviront aussi sur l'appli mobile.",
  },
  {
    icon: MousePointerClick,
    num: '02',
    title: 'Choisis ton cours',
    desc: "WOD CrossFit, HYROX, Team WOD, Free Access ou Team XL — sélectionne le créneau qui te va. Les places disponibles s'affichent en temps réel.",
  },
  {
    icon: CheckCircle2,
    num: '03',
    title: 'Confirme et viens',
    desc: "Ta réservation est instantanée. Tu reçois une confirmation par email et un rappel avant le cours. Annulation possible jusqu'à 2h avant sans frais.",
  },
];

const RESERVATION_FAQS = [
  {
    q: 'Je suis déjà abonné, dois-je créer un compte Deciplus ?',
    a: "Oui, la première fois. Si tu es abonné mais n'as jamais utilisé Deciplus, crée ton compte avec l'email fourni à l'inscription — ton abonnement sera automatiquement associé. Tes cours déjà réservés ou ton planning apparaîtront immédiatement.",
  },
  {
    q: 'Je ne suis pas encore membre, puis-je réserver un essai gratuit ici ?',
    a: "Pour le tout premier cours d'essai gratuit, passe par la page Contact — c'est plus simple, on te répond en moins de 24h pour caler le créneau ensemble. Après ton essai, tu passeras naturellement par la réservation en ligne.",
  },
  {
    q: "Comment annuler une réservation ?",
    a: "Depuis ton espace membre Deciplus (ici-même ou dans l'application mobile), tu retrouves tes réservations et tu peux annuler en un clic. Gratuit jusqu'à 2h avant le cours. Passé ce délai, la place reste décomptée de ton forfait.",
  },
  {
    q: "Puis-je réserver en Drop-in (visiteur) ?",
    a: "Oui ! Sélectionne la boutique dans l'espace Deciplus, achète ton Drop-in (1 séance, 25€) et réserve ta place directement. Parfait si tu es de passage à Saint-Chamond ou dans la vallée du Gier.",
  },
  {
    q: "L'application mobile est-elle disponible ?",
    a: "Oui — l'application Xplor Active (ex-Deciplus) est dispo sur iOS et Android. Télécharge-la, connecte-toi avec tes identifiants, et tu gères tout depuis ton téléphone : réservation, annulation, suivi de ton abonnement.",
  },
];

export default function ReservationPage() {
  const [cfg, setCfg] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content/reservation-config`)
      .then((r) => setCfg({ ...DEFAULT_CFG, ...(r.data || {}) }))
      .catch(() => setCfg(DEFAULT_CFG));
  }, []);

  const active = cfg?.enabled && cfg?.embed_url;

  return (
    <>
      <PageMeta
        title="Réserver un cours · CrossFit Saint-Chamond"
        description="Réserve ton prochain cours de CrossFit, HYROX ou Team WOD à Saint-Chamond en quelques clics. Planning en temps réel, réservation en 30 secondes, annulation gratuite jusqu'à 2h avant."
        path="/reservation"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Réservation' }]} />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/reservation"
        eyebrow="Réservation en ligne"
        title="RÉSERVE TON COURS"
        highlight="COURS"
        subtitle="Consulte le planning en temps réel et réserve ta place en moins de 30 secondes. Tout passe directement dans notre logiciel Deciplus."
        image={BOX_IMAGES.barbellPrep}
        alt="Réservation de cours CrossFit et HYROX à Saint-Chamond"
        breadcrumbs={[
          { name: 'Accueil', href: '/' },
          { name: 'Réservation' },
        ]}
        minHeight="min-h-[50vh]"
      />

      {/* ─────────────────────────────────────── HOW IT WORKS ─────────────────────────────────────── */}
      <section className="section-padding bg-white" data-testid="reservation-how-it-works">
        <div className="container-custom">
          <div className="max-w-2xl mb-14">
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
              ━━ Comment ça marche
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-3 leading-[0.95]">
              3 ÉTAPES,{' '}
              <span className="text-gradient-primary italic font-display">30 SECONDES</span>.
            </h2>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed mt-5">
              Premier cours ou centième : la réservation est la même. Rapide, claire, et tu gardes la main sur ton planning depuis ton téléphone ou ton ordi.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {HOW_STEPS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5%' }}
                  transition={{ duration: 0.7, delay: idx * 0.12, ease: EASE }}
                  data-testid={`reservation-step-${idx}`}
                  className="relative bg-accent/40 border border-border/60 rounded-2xl p-7 hover:border-primary/40 hover:bg-white hover:shadow-[0_20px_60px_-20px_hsla(155,75%,18%,0.25)] transition-all duration-500"
                >
                  <div className="font-display text-5xl text-primary/20 leading-none mb-5">
                    {step.num}
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={2.2} />
                  </div>
                  <h3 className="font-heading text-xl text-dark mb-3 tracking-wide">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── BOOKING WIDGET ─────────────────────────────────────── */}
      <section className="relative pb-20 md:pb-28 bg-white" data-testid="reservation-widget-section">
        {/* Decorative background */}
        <div className="absolute inset-x-0 top-0 h-[45%] bg-gradient-to-b from-dark via-dark to-primary-dark pointer-events-none" />
        <div className="absolute inset-x-0 top-10 mesh-gradient opacity-25 h-[35%] pointer-events-none" />

        <div className="container-custom relative z-10">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-md border border-white/20 text-primary-glow text-xs font-bold tracking-[0.22em] uppercase"
              data-testid="reservation-live-badge"
            >
              <span className="w-2 h-2 rounded-full bg-primary-glow animate-pulse" />
              Planning en direct · Deciplus
            </span>
          </div>
          <h2 className="text-center font-heading text-3xl md:text-5xl text-white leading-[1.05] mb-3">
            TON <span className="text-gradient-primary italic font-display">PLANNING</span>, EN TEMPS RÉEL.
          </h2>
          <p className="text-center text-white/65 text-sm md:text-base max-w-xl mx-auto mb-10">
            Le widget ci-dessous est connecté directement à notre logiciel Deciplus. Chaque réservation arrive instantanément dans notre box.
          </p>

          {/* Booking widget or fallback */}
          {cfg === null ? (
            <div className="h-[800px] flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-center">
                <div className="animate-pulse text-white/40 text-sm tracking-[0.22em] uppercase mb-2">
                  Chargement du planning
                </div>
                <div className="flex gap-1 justify-center">
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            </div>
          ) : active ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative rounded-[24px] overflow-hidden border border-white/15 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.5)] bg-white ring-1 ring-primary/10"
              data-testid="reservation-iframe-wrapper"
            >
              {/* Top toolbar (light, minimal) */}
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <span className="hidden sm:inline text-xs text-muted-foreground tracking-wider font-mono truncate max-w-[280px] md:max-w-md">
                    {cfg.embed_url.replace('https://', '')}
                  </span>
                </div>
                <a
                  href={cfg.embed_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                  data-testid="reservation-external-link"
                >
                  Ouvrir en plein écran
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <DeciplusIframe
                src={cfg.embed_url}
                title={`Réservation ${cfg.provider_name}`}
                defaultHeight={cfg.height_px || 1100}
                dataTestId="reservation-iframe"
              />
            </motion.div>
          ) : (
            <FallbackCTA cfg={cfg} />
          )}

          {/* Reassurance strip — under the iframe, light theme */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {[
              { icon: Clock, label: 'Places en temps réel', sub: "Mise à jour instantanée" },
              { icon: ShieldCheck, label: 'Paiement sécurisé', sub: 'Via plateforme Deciplus' },
              { icon: CalendarCheck, label: 'Annulation gratuite', sub: "Jusqu'à 2h avant le cours" },
              { icon: Sparkles, label: "Données protégées", sub: 'Hébergées chez Deciplus (RGPD)' },
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`reservation-trust-${i}`}
                className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-4.5 w-4.5 text-primary" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-heading text-sm tracking-wide text-dark">
                    {item.label}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── FIRST-TIMER CTA ─────────────────────────────────────── */}
      <section className="section-padding bg-accent/40" data-testid="reservation-firsttimer-section">
        <div className="container-custom">
          <div className="relative rounded-3xl overflow-hidden border border-border bg-white shadow-lg">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
            <div className="grid lg:grid-cols-5 gap-0 relative z-10">
              {/* Image */}
              <div className="lg:col-span-2 relative min-h-[280px] lg:min-h-[380px] overflow-hidden">
                <img decoding="async"
                  src={BOX_IMAGES.highFive}
                  alt="Premier cours d'essai gratuit — CrossFit Saint-Chamond"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-dark/70 via-dark/30 to-transparent lg:bg-gradient-to-r" />
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white text-[10px] font-bold tracking-[0.22em] uppercase">
                  <Sparkles className="h-3 w-3" /> 1ʳᵉ fois ?
                </div>
              </div>

              {/* Content */}
              <div className="lg:col-span-3 p-8 md:p-12">
                <h2 className="font-heading text-3xl md:text-4xl text-dark leading-tight mb-4">
                  Pas encore membre ?{' '}
                  <span className="text-gradient-primary italic font-display">Teste-nous</span>{' '}
                  gratuitement.
                </h2>
                <p className="text-muted-foreground text-base leading-relaxed mb-7 max-w-xl">
                  Avant de créer ton compte Deciplus, viens vivre une séance gratuite avec un coach. On te fait visiter la box, on discute de tes objectifs, et tu décides si tu veux continuer — sans engagement.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/contact" data-testid="reservation-firsttimer-cta">
                    <Button
                      size="lg"
                      className="btn-shine w-full sm:w-auto bg-primary hover:bg-primary-light text-white font-heading tracking-wider text-sm px-7 py-6 rounded-xl glow-primary group"
                    >
                      RÉSERVER MON ESSAI GRATUIT
                      <CalendarCheck className="ml-2 h-4 w-4 transition-transform group-hover:scale-110" />
                    </Button>
                  </Link>
                  <a href="mailto:crossfit.saintchamond@gmail.com" data-testid="reservation-email-link">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full sm:w-auto border-2 border-border hover:border-primary text-dark font-heading tracking-wider text-sm px-7 py-6 rounded-xl group"
                    >
                      <Mail className="mr-2 h-4 w-4" /> Nous écrire
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────── FAQ ─────────────────────────────────────── */}
      <section className="section-padding bg-white" data-testid="reservation-faq">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
              ━━ Besoin d'aide
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl text-dark mt-3 mb-3 leading-[1]">
              QUESTIONS SUR LA{' '}
              <span className="text-gradient-primary italic font-display">RÉSERVATION</span>
            </h2>
          </div>

          <div className="space-y-3">
            {RESERVATION_FAQS.map((faq, idx) => {
              const open = openFaq === idx;
              return (
                <div
                  key={idx}
                  data-testid={`reservation-faq-item-${idx}`}
                  className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                    open ? 'border-primary/40 shadow-md bg-white' : 'border-border bg-accent/30'
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(open ? null : idx)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white transition-colors"
                    data-testid={`reservation-faq-toggle-${idx}`}
                    aria-expanded={open}
                  >
                    <span className="font-semibold text-base pr-4">{faq.q}</span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${
                        open ? 'rotate-180 text-primary' : 'text-muted-foreground'
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      open ? 'max-h-[400px]' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-5">
                      <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* FAQ JSON-LD */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: RESERVATION_FAQS.map((f) => ({
                  '@type': 'Question',
                  name: f.q,
                  acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
              }),
            }}
          />

          {/* Still need help */}
          <div className="mt-14 text-center border-t border-border pt-10">
            <p className="text-sm text-muted-foreground mb-5">
              Tu ne trouves pas la réponse ? Contacte-nous, on répond en moins de 24h.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact" data-testid="reservation-contact-link">
                <Button variant="outline" className="gap-2 border-2">
                  <Phone className="h-4 w-4" /> Formulaire contact
                </Button>
              </Link>
              <a href="mailto:crossfit.saintchamond@gmail.com">
                <Button variant="outline" className="gap-2 border-2">
                  <Mail className="h-4 w-4" /> crossfit.saintchamond@gmail.com
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

function FallbackCTA({ cfg }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md p-10 md:p-16 text-center"
      data-testid="reservation-fallback"
    >
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative z-10 max-w-2xl mx-auto">
        <span className="inline-block text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase mb-4">
          ━━ Bientôt disponible
        </span>
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-5">
          Réservation en ligne{' '}
          <span className="text-gradient-primary italic font-display">
            en cours d'activation
          </span>
        </h2>
        <p className="text-white/70 leading-relaxed mb-8">
          {cfg?.fallback_message || DEFAULT_CFG.fallback_message}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/contact" data-testid="reservation-fallback-cta">
            <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg">
              {cfg?.fallback_cta_label || DEFAULT_CFG.fallback_cta_label}
            </Button>
          </Link>
          <Link to="/planning" data-testid="reservation-see-planning">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg"
            >
              Voir le planning
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
